/**
 * LabImpactPanel.jsx — iter883 (Feb 2026)
 *
 * "🩸 Импакт на последната лаборатория" — post-upload link visualisation.
 *
 * For a specific lab_analysis, shows:
 *   • Suggested conditions derived from abnormal markers (ESC/EAS,
 *     ATA, Endocrine Society thresholds) with chip-accept flow that
 *     writes to `health_conditions`.
 *   • KEGG pathway chips per suggested condition (link-out to kegg.jp).
 *   • 6 tissue-microenvironment before/after tiles with Δ and the new
 *     "signals we can change" that appeared after the upload.
 *
 * Fetched lazily via GET /api/documents/labs/{lab_id}/impact.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, CheckCircle2, Sparkles, ExternalLink, TrendingDown,
  TrendingUp, Activity, Droplets, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { emitHealthEvent } from "@/utils/healthEvents";
import { toast } from "sonner";

const TX = {
  bg: {
    title: "🩸 Импакт на тази лаборатория",
    subtitle:
      "Как маркерите от този файл влияят върху Вашата биология. Приемете диагнозите, за да участват в BHI изчисленията.",
    loading: "Пресмятаме импакта…",
    empty:
      "Няма изведени сигнали от този файл — стойностите са в референтни граници или маркерите не отговарят на клиничните прагове.",
    suggestedTitle: "🧬 AI-изведени диагнози → KEGG пътища",
    zonesTitle: "🏥 Тъкани, които тази лаборатория повлия",
    accept: "Приеми",
    acceptAll: "Приеми всички",
    accepted: "Приета",
    acceptSuccess: "Диагнозите са добавени",
    acceptError: "Грешка при приемане",
    beforeAfter: "преди → сега",
    newFactor: "нов сигнал",
    resolvedFactor: "решен сигнал",
    severity: { mild: "лека", moderate: "умерена", severe: "тежка" },
    evidence: "Основание",
    noData: "недостатъчно данни",
  },
  en: {
    title: "🩸 Impact of this lab",
    subtitle:
      "How the markers in this file influence your biology. Accept the diagnoses to include them in BHI.",
    loading: "Computing impact…",
    empty:
      "No signals derived — values are within reference range or below clinical cutoffs.",
    suggestedTitle: "🧬 AI-derived diagnoses → KEGG pathways",
    zonesTitle: "🏥 Tissues affected by this lab",
    accept: "Accept",
    acceptAll: "Accept all",
    accepted: "Accepted",
    acceptSuccess: "Diagnoses added",
    acceptError: "Accept failed",
    beforeAfter: "before → now",
    newFactor: "new signal",
    resolvedFactor: "resolved signal",
    severity: { mild: "mild", moderate: "moderate", severe: "severe" },
    evidence: "Evidence",
    noData: "insufficient data",
  },
};

const SEVERITY_COLOR = {
  mild:     "bg-amber-500/20  text-amber-600  dark:text-amber-300",
  moderate: "bg-orange-500/20 text-orange-600 dark:text-orange-300",
  severe:   "bg-red-500/20    text-red-600    dark:text-red-300",
};

export default function LabImpactPanel({ labId, lang = "bg", compact = false }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [accepted, setAccepted] = useState([]);

  const load = useCallback(async () => {
    if (!labId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents/labs/${labId}/impact`);
      setData(data);
      setAccepted(data.accepted_condition_ids || []);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API, labId]);
  useEffect(() => { load(); }, [load]);

  const acceptConds = async (ids) => {
    setBusy("accept");
    try {
      const { data: res } = await axios.post(
        `${API}/documents/labs/${labId}/accept-conditions`,
        ids ? { condition_ids: ids } : {},
      );
      setAccepted(res.accepted_condition_ids || []);
      toast.success(`${t.acceptSuccess} (${res.count})`);
      emitHealthEvent("conditions-changed", { source: "lab-impact-accept" });
    } catch (e) {
      toast.error(t.acceptError);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-3 text-xs opacity-70 flex items-center gap-2" data-testid="lab-impact-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> {t.loading}
      </div>
    );
  }
  if (!data) return null;

  const suggested = Array.isArray(data.suggested_conditions) ? data.suggested_conditions : [];
  const pendingCount = suggested.filter((s) => !accepted.includes(s.condition_id)).length;
  const zones = Array.isArray(data.zones) ? data.zones : [];
  // Zones worth surfacing: those with a delta OR new factors
  const surfacedZones = zones.filter(
    (z) => (z.delta !== null && z.delta !== 0) || (z.new_factors && z.new_factors.length > 0),
  );

  return (
    <div className={`p-3 rounded-xl border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`} data-testid="lab-impact-panel">
      <div className="flex items-start gap-2 mb-2">
        <Droplets className="w-4 h-4 text-pink-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-extrabold">{t.title}</div>
          {!compact && (
            <p className="text-[10px] opacity-70 leading-relaxed mt-0.5">{t.subtitle}</p>
          )}
        </div>
      </div>

      {/* ── Derived diagnoses → KEGG chips ─────────────────────── */}
      {suggested.length === 0 ? (
        <div className="text-[11px] opacity-60 py-2">{t.empty}</div>
      ) : (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-pink-500" />
              <span className="text-[11px] font-bold">{t.suggestedTitle}</span>
            </div>
            {pendingCount > 1 && (
              <button
                onClick={() => acceptConds(null)}
                disabled={busy === "accept"}
                className="px-2 py-0.5 rounded-md text-[10px] font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 disabled:opacity-60 flex items-center gap-1"
                data-testid="lab-impact-accept-all"
              >
                {busy === "accept" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {t.acceptAll}
              </button>
            )}
          </div>
          <ul className="space-y-2">
            {suggested.map((s) => {
              const isAccepted = accepted.includes(s.condition_id);
              return (
                <li
                  key={s.condition_id}
                  className={`p-2 rounded-lg border ${
                    isAccepted
                      ? "border-emerald-500/40 bg-emerald-500/10"
                      : (isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white")
                  }`}
                  data-testid={`lab-impact-cond-${s.condition_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                      <span className="text-[11px] font-extrabold truncate">{s.name_bg}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${SEVERITY_COLOR[s.severity] || SEVERITY_COLOR.mild}`}>
                        {t.severity[s.severity] || s.severity}
                      </span>
                      {isAccepted && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] text-emerald-600 dark:text-emerald-300 font-bold shrink-0">
                          <CheckCircle2 className="w-2.5 h-2.5" /> {t.accepted}
                        </span>
                      )}
                    </div>
                    {!isAccepted && (
                      <button
                        onClick={() => acceptConds([s.condition_id])}
                        disabled={busy === "accept"}
                        className="px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-pink-500/20 hover:bg-pink-500/30 text-pink-600 dark:text-pink-300 disabled:opacity-60 flex items-center gap-1 shrink-0"
                        data-testid={`lab-impact-accept-${s.condition_id}`}
                      >
                        {busy === "accept" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                        {t.accept}
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] opacity-75 leading-snug mb-1">{s.reason}</p>
                  {Array.isArray(s.evidence) && s.evidence.length > 0 && (
                    <div className="text-[10px] opacity-70 mb-1">
                      <b>{t.evidence}:</b>{" "}
                      {s.evidence.map((e, i) => (
                        <span key={i}>
                          {e.code} = {typeof e.value === "number" ? e.value.toFixed(2) : e.value}{" "}
                          <span className="opacity-60">({e.cutoff})</span>
                          {i < s.evidence.length - 1 ? "; " : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(s.matching_pathways) && s.matching_pathways.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {s.matching_pathways.map((p) => (
                        <a
                          key={p.id}
                          href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : undefined}
                          target={p.kegg ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                            isDark ? "bg-white/10 hover:bg-white/15 text-blue-200" : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                          }`}
                          title={p.name_bg}
                        >
                          {p.name_bg}
                          {p.kegg && <ExternalLink className="w-2 h-2 opacity-60" />}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Tissue-zone before/after ───────────────────────────── */}
      {surfacedZones.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Activity className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-[11px] font-bold">{t.zonesTitle}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {surfacedZones.map((z) => {
              const delta = typeof z.delta === "number" ? z.delta : null;
              const trendDown = delta !== null && delta < 0;
              const trendUp = delta !== null && delta > 0;
              return (
                <div
                  key={z.zone}
                  className={`p-2 rounded-lg border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"}`}
                  data-testid={`lab-impact-zone-${z.zone}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[11px] font-bold truncate">{z.name_bg}</span>
                    <div className="flex items-center gap-1 text-[10px]">
                      <span className="opacity-60">
                        {z.before_score ?? "—"}
                        <ArrowRight className="w-2.5 h-2.5 inline mx-0.5 opacity-50" />
                        {z.after_score ?? "—"}
                      </span>
                      {delta !== null && delta !== 0 && (
                        <span className={`inline-flex items-center gap-0.5 font-bold ${trendDown ? "text-red-500" : "text-emerald-500"}`}>
                          {trendDown ? <TrendingDown className="w-2.5 h-2.5" /> : <TrendingUp className="w-2.5 h-2.5" />}
                          {delta > 0 ? "+" : ""}{delta}
                        </span>
                      )}
                    </div>
                  </div>
                  {Array.isArray(z.new_factors) && z.new_factors.length > 0 && (
                    <ul className="text-[10px] opacity-80 space-y-0.5">
                      {z.new_factors.slice(0, 3).map((f, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-pink-500 shrink-0">•</span>
                          <span className="leading-tight">{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
